/**
 * The two pages of a password reset: asking for a link, and spending one.
 *
 * They live in one file because they are one flow and share the same card, and
 * because the wording on each depends on the other. Two rules run through
 * both:
 *
 * **Neither page ever says whether an address has an account.** The request
 * page shows the server's own sentence rather than composing one, so a future
 * edit here cannot turn "if that address has an account" into "we've emailed
 * you" — which would make this form a way to find out who is registered.
 *
 * **Success is a destination, not a banner.** A reset ends every session on
 * the account, so the page that follows it must be the sign-in form: leaving
 * someone on a confirmation with no next step, after an action that logged
 * them out everywhere, is how "did that work?" happens.
 */

import { useState, type FormEvent } from "react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Link, navigate, useSearchParam } from "../router";
import { FORGOT_PATH, SIGNIN_PATH } from "../lib/route";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  // The server's sentence, held rather than written. See the module docstring.
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const result = await api.requestPasswordReset(email);
      setSent(result.detail);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="gate">
      <div className="card">
        <h1>
          <span>D</span>is<span>TR</span>ess Signal
        </h1>

        {sent ? (
          <>
            <p className="lede">{sent}</p>
            <p className="sub">
              Nothing arrived? Check the spam folder, then try again — asking
              twice replaces the first link rather than sending a second one
              that also works.
            </p>
            <p className="switch">
              <Link to={SIGNIN_PATH} className="linkbtn">
                Back to sign in
              </Link>
            </p>
          </>
        ) : (
          <>
            <p className="lede">
              <b>Forgotten your password?</b> Give us the address on the
              account and we'll email a link to set a new one.
            </p>

            {error && (
              <div className="err" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={submit}>
              <label className="field">
                <span>Email</span>
                <input
                  type="email"
                  value={email}
                  required
                  autoComplete="username"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>

              <div style={{ height: 20 }} />
              <button className="btn" disabled={busy}>
                {busy ? "Working…" : "Email me a link"}
              </button>
            </form>

            <p className="switch">
              Remembered it?{" "}
              <Link to={SIGNIN_PATH} className="linkbtn">
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export function ResetPassword() {
  const { signOut } = useAuth();
  const token = useSearchParam("token");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      // Drop this device's token first. The server has already revoked it —
      // the reset rotated the session id — so a client that goes on believing
      // it is signed in would be bounced from /signin to the dashboard, fail
      // its first request, and land back here reading "your session expired"
      // instead of "password changed". Two true statements, and the wrong one
      // shown.
      signOut();
      // Then the form, because there is no session to return to. The outcome
      // rides in the URL rather than being left behind: dropping someone on a
      // bare sign-in form after a change they cannot see is how "did that
      // work?" happens, and they have just typed a password they are not yet
      // sure took effect.
      navigate(`${SIGNIN_PATH}?reset=done`);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Something went wrong.",
      );
      setBusy(false);
    }
  }

  // A link with nothing after `token=` is a link that was truncated in a mail
  // client. Say so here rather than posting an empty token and rendering the
  // server's "expired or already used", which would send someone looking for a
  // link that has not in fact expired.
  if (!token) {
    return (
      <div className="gate">
        <div className="card">
          <h1>
            <span>D</span>is<span>TR</span>ess Signal
          </h1>
          <p className="lede">
            <b>That link is incomplete.</b> Copy the whole address out of the
            email — some mail clients break long ones across two lines — or ask
            for a new one.
          </p>
          <p className="switch">
            <Link to={FORGOT_PATH} className="linkbtn">
              Send me another link
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="gate">
      <div className="card">
        <h1>
          <span>D</span>is<span>TR</span>ess Signal
        </h1>
        <p className="lede">
          <b>Choose a new password.</b> Setting it signs out every device on
          this account, including any you did not recognise.
        </p>

        {error && (
          <div className="err" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={submit}>
          <label className="field">
            <span>New password</span>
            <input
              type="password"
              value={password}
              required
              minLength={8}
              autoComplete="new-password"
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          <div style={{ height: 20 }} />
          <button className="btn" disabled={busy}>
            {busy ? "Working…" : "Set password and sign out everywhere"}
          </button>
        </form>

        <p className="switch">
          <Link to={FORGOT_PATH} className="linkbtn">
            Ask for a new link
          </Link>
        </p>
      </div>
    </div>
  );
}
